import React, { useEffect } from "react";
import { Grid, Stack } from "@mantine/core";
import Checkbox from "@/components/atoms/Checkbox";
import styles from "../styles.module.css";
import OrderStatusEnum from "@/constants/enums/OrderStatusEnum";
import OrderTypeEnum from "@/constants/enums/OrderTypeEnum";
import SelectProductMaterial from "@/components/molecules/SelectInputs/SelectProductMaterial";
import Group from "@/components/atoms/Group";
import { useOrderStore } from "@/stores/ordersStore";
import { useTranslation } from "react-i18next";
import SortButton from "@/components/atoms/SortButton";
import MultiSelectOrderStatus from "@/components/molecules/SelectInputs/MultiSelectOrderStatus";
import MultiSelectOrderTypes from "@/components/molecules/SelectInputs/MultiSelectOrderTypes";
import { useSearchParams } from "react-router-dom";
import { OrderStatus } from "@/types";

const OrdersFilter = () => {
  const sortDirection = useOrderStore((state) => state.sortDirection);
  const setSortDirection = useOrderStore((state) => state.setSortDirection);
  const sortBy = useOrderStore((state) => state.sortBy);
  const setSortBy = useOrderStore((state) => state.setSortBy);
  const orderTypes = useOrderStore((state) => state.orderTypes);
  const setOrderTypes = useOrderStore((state) => state.setOrderTypes);
  const statuses = useOrderStore((state) => state.statuses);
  const setStatuses = useOrderStore((state) => state.setStatuses);
  const salesItemId = useOrderStore((state) => state.salesItemId);
  const setSalesItemId = useOrderStore((state) => state.setSalesItemId);
  const awaitingProcessingOnly = useOrderStore((state) => state.awaitingProccessingOnly);
  const setAwaitingProccessingOnly = useOrderStore((state) => state.setAwaitingProccessingOnly);
  const excludeContractOrders = useOrderStore((state) => state.excludeContractOrders);
  const setExcludeContractOrders = useOrderStore((state) => state.setExcludeContractOrders);
  const includeInactive = useOrderStore((state) => state.includeInactive);
  const setIncludeInactive = useOrderStore((state) => state.setIncludeInactive);
  const currentPage = useOrderStore((state) => state.currentPage);
  const setPage = useOrderStore((state) => state.setPage);
  const limit = useOrderStore((state) => state.limit);
  const setLimit = useOrderStore((state) => state.setLimit);

  /* Get filter, sort and pagination options from URL or session storage */
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const KEY = "orders_filters_v1";
    const statusParams = searchParams.getAll("status");
    const typeParams = searchParams.getAll("type");
    const salesItem = searchParams.get("salesItem");
    const awaiting = searchParams.get("awaiting");
    const excludeContract = searchParams.get("excludeContract");
    const inactive = searchParams.get("inactive");
    const sort = searchParams.get("sort");
    const dir = searchParams.get("dir");
    const page = searchParams.get("page");
    const pageLimit = searchParams.get("limit");

    const hasParams = Boolean(
      statusParams.length > 0 ||
        typeParams.length > 0 ||
        salesItem ||
        awaiting ||
        excludeContract ||
        inactive ||
        sort ||
        dir ||
        page ||
        pageLimit
    );

    let paramsToUse = searchParams;
    let isPersisted = hasParams;

    if (!hasParams) {
      try {
        const saved = sessionStorage.getItem(KEY);
        if (saved) {
          const savedSP = new URLSearchParams(saved);
          paramsToUse = savedSP as unknown as URLSearchParams;
          isPersisted = true;
          setSearchParams(savedSP, { replace: true });
        }
      } catch {
        // ignored
      }
    }

    // On a truly virgin load (no URL params, nothing saved) leave the store defaults untouched.
    if (!isPersisted) return;

    const statusParams2 = paramsToUse.getAll("status");
    const typeParams2 = paramsToUse.getAll("type");
    const salesItem2 = paramsToUse.get("salesItem");
    const awaiting2 = paramsToUse.get("awaiting");
    const excludeContract2 = paramsToUse.get("excludeContract");
    const inactive2 = paramsToUse.get("inactive");
    const sort2 = paramsToUse.get("sort");
    const dir2 = paramsToUse.get("dir");
    const page2 = paramsToUse.get("page");
    const pageLimit2 = paramsToUse.get("limit");

    // Once we're hydrating from a persisted source, it is the source of truth —
    // absence of a param means "cleared", not "fall back to the store default".
    setStatuses(statusParams2.length > 0 ? (statusParams2 as OrderStatus[]) : []);
    setOrderTypes(typeParams2.length > 0 ? typeParams2.map((t) => Number(t)) : null);
    setSalesItemId(salesItem2 ? Number(salesItem2) : null);
    setAwaitingProccessingOnly(awaiting2 === "true");
    setExcludeContractOrders(excludeContract2 === "true");
    setIncludeInactive(inactive2 === "true");
    setSortBy(sort2 || "date");
    setSortDirection(dir2?.toUpperCase() === "ASC" ? "ASC" : "DESC");
    setPage(page2 ? Number(page2) : 1);
    setLimit(pageLimit2 ? Number(pageLimit2) : 15);
  }, []);

  /* Set filter, sort and pagination options to URL and session storage */
  useEffect(() => {
    const KEY = "orders_filters_v1";
    const sp = new URLSearchParams();
    if (statuses && statuses.length > 0) {
      statuses.forEach((s) => sp.append("status", s));
    }
    if (orderTypes && orderTypes.length > 0) {
      orderTypes.forEach((t) => sp.append("type", String(t)));
    }
    if (salesItemId) sp.set("salesItem", String(salesItemId));
    if (awaitingProcessingOnly) sp.set("awaiting", "true");
    if (excludeContractOrders) sp.set("excludeContract", "true");
    if (includeInactive) sp.set("inactive", "true");
    if (sortBy) sp.set("sort", sortBy);
    if (sortDirection) sp.set("dir", sortDirection.toLowerCase());
    if (currentPage) sp.set("page", String(currentPage));
    if (limit) sp.set("limit", String(limit));

    setSearchParams(sp, { replace: true });

    try {
      sessionStorage.setItem(KEY, sp.toString());
    } catch {
      // ignore
    }
  }, [
    statuses,
    orderTypes,
    salesItemId,
    awaitingProcessingOnly,
    excludeContractOrders,
    includeInactive,
    sortBy,
    sortDirection,
    currentPage,
    limit,
    setSearchParams,
  ]);

  const changeDirection = () => {
    if (sortDirection === "ASC") {
      setSortDirection("DESC");
    } else {
      setSortDirection("ASC");
    }
  };

  const { t } = useTranslation();

  return (
    <Grid className={styles.ordersFilter} align="end">
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <Stack>
          <MultiSelectOrderStatus
            label={t("orders:orderStatus")}
            value={statuses}
            onChange={(val: string[]) => setStatuses(val as OrderStatusEnum[])}
          />
        </Stack>
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <MultiSelectOrderTypes
          label={t("orders:orderType")}
          value={orderTypes ? orderTypes.map((ot) => ot.toString()) : []}
          onChange={(val: any) => setOrderTypes(val as OrderTypeEnum[])}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, sm: 6, lg: 2 }}>
        <SelectProductMaterial
          label={t("product:material")}
          valueKey={"id"}
          labelKey={"value"}
          onChange={(val) => (val ? setSalesItemId(Number(val)) : null)}
          value={salesItemId?.toString()}
        />
      </Grid.Col>
      <Grid.Col span={12}>
        <Group wrap={"wrap"} align={"center"} justify={"flex-start"}>
          <SortButton onClick={() => changeDirection()} currentDirection={sortDirection} />
          <Checkbox
            label={t("actions:showInactive")}
            checked={includeInactive}
            onChange={(e) => setIncludeInactive(e.target.checked)}
          />
          <Checkbox
            label={t("actions:excludeContractOrders")}
            checked={excludeContractOrders}
            onChange={(e) => setExcludeContractOrders(e.target.checked)}
          />
          <Checkbox
            label={t("actions:awaitingProcessingOnly")}
            checked={awaitingProcessingOnly}
            onChange={(e) => setAwaitingProccessingOnly(e.target.checked)}
          />
        </Group>
      </Grid.Col>
    </Grid>
  );
};

export default OrdersFilter;
